import { publicEnv, frontendMode } from '@/config/runtime'
import {
  type AsyncComponentLoader,
  type Component,
  type ComponentPublicInstance,
  defineAsyncComponent,
} from 'vue'
import * as Sentry from '@sentry/vue'
import { Loading, LoadingError } from '@/components/misc/Loading'

/**
 * Returns the release version of the application.
 *
 * @return {string} The release version of the application.
 */
export function releaseVersion(): string {
  return __RELEASE_VER__
}

export function isProdBuild(): boolean {
  return frontendMode() === 'production'
}

export const API_URL = {
  v1: publicEnv('VUE_APP_ROOT_API') || '',
  v2: publicEnv('VUE_APP_ROOT_API_V2') || '',
  v3: publicEnv('VUE_APP_ROOT_API_V3') || '',
}

export function downloadFile(uri: string, filename: string) {
  const link = document.createElement('a')
  link.download = filename
  link.href = uri
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// RFC-4180: quote any field containing the `;` delimiter, a quote or a
// newline, doubling embedded quotes. Without this an embedded `";` would
// split the cell and let a following `=` run as a formula on open.
export function escapeCsvField(value: unknown): string {
  const str = value == null ? '' : String(value)
  if (/[";\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

export function downloadDataAsCsv(data: any[][], filename: string) {
  const csvContent =
    'data:text/csv;charset=utf-8,\uFEFF' +
    encodeURIComponent(
      data.map((row) => row.map(escapeCsvField).join(';')).join('\n')
    )

  downloadFile(csvContent, `${filename}.csv`)
}

export function downloadCanvasAsPng(
  canvas: HTMLCanvasElement,
  filename: string
) {
  canvas.toBlob((blob) => {
    if (blob) {
      const url = URL.createObjectURL(blob)

      downloadFile(url, `${filename}.png`)
      URL.revokeObjectURL(url)
    }
  }, 'image/png')
}

export function defineLoadingAsyncComponent<
  T extends Component = {
    new (): ComponentPublicInstance
  },
>(source: AsyncComponentLoader<T>): T {
  return defineAsyncComponent<T>({
    loader: source,
    loadingComponent: Loading,
    delay: 200,
    timeout: 5000,
    errorComponent: LoadingError,
    onError(error) {
      console.error('Failed to load component:', error)
      Sentry.captureException(error)
    },
  })
}
